import { cn } from '@/lib/utils';

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  /** A `stroke-*` utility. Literal, because a slice is a series colour, not a token. */
  stroke: string;
  /** The matching `bg-*` for the legend's dot. */
  dot: string;
}

export interface DonutChartProps {
  slices: DonutSlice[];
  /** The big number in the hole. */
  total: number;
  /** What that number counts — "Total staff". */
  caption: string;
  /**
   * How each figure is written — the total in the hole and every legend value.
   *
   * Defaults to the plain number, which is right for a ring counting things.
   * A ring dividing up money passes `formatCurrency`, so the legend reads
   * ₹3,65,000 rather than 365000 (CLAUDE.md §3: a prop, not a second chart).
   */
  formatValue?: (value: number) => string;
  /**
   * What the hole reads, when that is not the total of the slices.
   *
   * A ring dividing days into present, late, leave and absent is asked "how
   * much of it was attended?", and the answer is a percentage — not the sum of
   * the four, which is merely the number of days. Only the hole changes: the
   * legend keeps counting what it counted, so the two never disagree.
   */
  centerValue?: string;
  className?: string;
  layout?: 'vertical' | 'horizontal' | 'responsive';
}

/** The ring's geometry. One place, so the circumference below cannot drift. */
const RADIUS = 55;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * A ring of proportions, with the total in the middle.
 *
 * SVG rather than a charting library — this is one ring, and a dependency for it
 * would be a megabyte to draw four arcs (CLAUDE.md §1: no new dependencies).
 * Each slice is one circle with a dash pattern: a dash as long as its share and
 * a gap for the rest, rotated to where the previous one ended.
 *
 * The arcs grow from nothing on first paint — a transition on `stroke-dasharray`
 * rather than a keyframe, so it also animates when the numbers change under it
 * rather than only on load.
 *
 * Accessibility: the ring is `aria-hidden` and the legend beside it carries the
 * same numbers as text. A screen reader gets the figures, not a description of
 * a picture.
 */
export function DonutChart({
  slices,
  total,
  caption,
  formatValue = (value) => String(value),
  centerValue,
  className,
  layout = 'vertical',
}: DonutChartProps) {
  const counted = slices.reduce((running, slice) => running + slice.value, 0);
  const shown = slices.filter((slice) => slice.value > 0);

  let sweptSoFar = 0;
  const isHorizontal = layout === 'horizontal';

  return (
    <div
      className={cn(
        'w-full',
        isHorizontal
          ? 'flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-6'
          : 'flex flex-col items-center gap-4',
        className,
      )}
    >
      <div className="relative h-[140px] w-[140px] shrink-0 my-0.5">
        <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90" aria-hidden>
          {/* The track, so a ring with one small slice still reads as a ring. */}
          <circle
            cx="70"
            cy="70"
            r={RADIUS}
            fill="none"
            strokeWidth="12"
            className="stroke-slate-100 dark:stroke-slate-800"
          />

          {shown.map((slice) => {
            const share = counted > 0 ? slice.value / counted : 0;
            const dash = share * CIRCUMFERENCE;
            // Where the previous arc finished. Negative because the whole ring
            // is rotated a quarter turn to start at twelve o'clock.
            const offset = -sweptSoFar * CIRCUMFERENCE;
            sweptSoFar += share;

            return (
              <circle
                key={slice.key}
                cx="70"
                cy="70"
                r={RADIUS}
                fill="none"
                strokeWidth="12"
                strokeLinecap="butt"
                // The two values a ring cannot express as classes: both come
                // from the data and neither exists until runtime.
                style={{
                  strokeDasharray: `${dash} ${CIRCUMFERENCE - dash}`,
                  strokeDashoffset: offset,
                }}
                className={cn('transition-all duration-700 ease-out', slice.stroke)}
              />
            );
          })}
        </svg>

        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center p-2 text-center">
          <p className="max-w-[104px] truncate text-[15px] sm:text-[16px] font-black tabular-nums tracking-tight text-slate-800 dark:text-slate-100 leading-tight">
            {centerValue ?? formatValue(total)}
          </p>
          <p className="mt-0.5 text-[10px] font-extrabold tracking-wide text-slate-400 dark:text-slate-500 leading-none">
            {caption}
          </p>
        </div>
      </div>

      <ul
        className={cn(
          'flex flex-col gap-2.5 w-full',
          isHorizontal
            ? 'flex-1 pt-3 sm:pt-0 border-t sm:border-t-0 sm:border-l border-slate-100 dark:border-slate-800/80 sm:pl-5'
            : 'pt-3 border-t border-slate-100 dark:border-slate-800/80',
        )}
      >
        {slices.map((slice) => {
          const share = counted > 0 ? Math.round((slice.value / counted) * 100) : 0;

          return (
            <li key={slice.key} className="flex items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', slice.dot)} aria-hidden />
                <span className="truncate font-bold text-slate-700 dark:text-slate-200 text-xs sm:text-sm">
                  {slice.label}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0 font-mono">
                <span className="font-extrabold text-slate-900 dark:text-white tabular-nums text-xs sm:text-sm">
                  {formatValue(slice.value)}
                </span>
                <span className="rounded-md bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-right text-[11px] font-bold text-slate-500 dark:text-slate-400 tabular-nums">
                  {share}%
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
